import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  ChevronDown,
  Phone,
  Search,
  MapPin,
  BarChart3,
  Shield,
  Link2,
  FileText,
  Store,
  ShoppingBag,
  Briefcase,
  Sparkles,
  Zap,
  Globe,
  TrendingUp,
  Target,
  Layers,
  Code2,
  ArrowRight,
} from 'lucide-react';
import { navigationLinks } from '../mockData';
import { QuoteButton } from './QuoteModal';

/**
 * Mega-menu service catalogue. Grouped visually into columns with icons.
 */
const serviceGroups = [
  {
    label: 'SEO Services',
    items: [
      { name: 'SEO Services', path: '/seo-services', icon: Search, desc: 'Flagship SEO packages' },
      { name: 'Local SEO', path: '/local-seo', icon: MapPin, desc: 'Rank in your suburb' },
      { name: 'Enterprise SEO Services', path: '/enterprise-seo-services', icon: BarChart3, desc: 'For larger brands' },
      { name: 'SEO for Small Business', path: '/seo-for-small-business', icon: Briefcase, desc: 'Tailored for SMBs' },
      { name: 'eCommerce SEO', path: '/ecommerce-seo', icon: ShoppingBag, desc: 'Drive product sales' },
      { name: 'Shopify SEO', path: '/shopify-seo', icon: Store, desc: 'Shopify store SEO' },
      { name: 'WordPress SEO', path: '/wordpress-seo', icon: Code2, desc: 'WP optimisation' },
      { name: 'Lead Generation SEO', path: '/lead-generation-seo', icon: Target, desc: 'More qualified leads' },
    ],
  },
  {
    label: 'SEO Audits & Fixes',
    items: [
      { name: 'Website SEO Audit', path: '/website-seo-audit', icon: FileText, desc: 'FREE audit' },
      { name: 'Backlink Audit', path: '/backlink-audit', icon: Shield, desc: 'Toxic link cleanup' },
      { name: 'Competitor Analysis', path: '/competitor-analysis', icon: Layers, desc: 'Benchmark rivals' },
      { name: 'Google Penalty Removal', path: '/google-penalty-removal', icon: Zap, desc: 'Recover lost rankings' },
      { name: 'Link Building Services', path: '/link-building-services', icon: Link2, desc: 'Premium backlinks' },
      { name: 'Content Marketing', path: '/content-marketing-services', icon: Sparkles, desc: 'Words that rank' },
      { name: 'White Label SEO', path: '/white-label-seo', icon: Globe, desc: 'For agencies' },
      { name: 'SEO Reseller Services', path: '/seo-reseller-services', icon: TrendingUp, desc: 'Resell GWM SEO' },
    ],
  },
  {
    label: 'Paid, Web & Digital',
    items: [
      { name: 'Google Ads Management', path: '/google-ads-management', icon: Target, desc: 'PPC that converts' },
      { name: 'PPC / AdWords Management', path: '/pay-per-click-ppc-adwords-management', icon: Zap, desc: 'AdWords mgmt' },
      { name: 'Remarketing', path: '/remarketing', icon: TrendingUp, desc: 'Win back visitors' },
      { name: 'Digital Marketing', path: '/digital-marketing-services', icon: BarChart3, desc: 'Full-funnel growth' },
      { name: 'Landing Page Design', path: '/landing-page-design', icon: Layers, desc: 'High-convert pages' },
      { name: 'HTTPS Migration', path: '/https-migration', icon: Shield, desc: 'Secure your site' },
      { name: 'Online Reputation', path: '/online-reputation-management', icon: Sparkles, desc: 'Manage your brand' },
      { name: 'SEO (legacy)', path: '/seo', icon: Search, desc: 'Original SEO page' },
    ],
  },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location]);

  // Hover handlers with delay to prevent flicker
  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setIsServicesOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setIsServicesOpen(false), 150);
  };

  return (
    <>
      {/* Top utility bar (desktop) */}
      <div
        className={`hidden lg:block fixed top-0 left-0 right-0 z-[60] bg-slate-900 text-slate-200 text-xs transition-all duration-300 ${
          isScrolled ? '-translate-y-full opacity-0' : 'translate-y-0 opacity-100'
        }`}
        data-testid="top-utility-bar"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-9">
            <div className="flex items-center gap-5">
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e] animate-pulse" />
                Australia's #1 SEO Agency · 1000+ Businesses Since 2016
              </span>
            </div>
            <div className="flex items-center gap-5">
              <a
                href="tel:1800006296"
                className="inline-flex items-center gap-1.5 hover:text-[#22c55e] transition-colors font-semibold"
                data-testid="top-bar-phone"
              >
                <Phone size={12} /> 1800 006 296
              </a>
            </div>
          </div>
        </div>
      </div>

      <header
        className={`fixed left-0 right-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? 'top-0 backdrop-blur-xl bg-white/90 border-b border-slate-200/60 shadow-sm'
            : 'top-9 bg-white/90 backdrop-blur-sm'
        }`}
        data-testid="header"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 transition-opacity hover:opacity-85"
              data-testid="logo-link"
            >
              <img
                src="/assets/logo.webp"
                alt="GWM — Green Web Marketing"
                className="h-11 w-11 md:h-12 md:w-12 object-contain rounded-lg"
                loading="eager"
              />
              <span className="hidden sm:block">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 leading-none">
                  Green Web Marketing
                </span>
                <span className="block text-xl font-black text-slate-900 tracking-tight leading-tight">
                  GWM
                  <span className="text-[#15803d]">.</span>
                </span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
              <Link
                to="/"
                className="text-[15px] text-slate-700 hover:text-[#15803d] font-semibold transition-colors"
              >
                Home
              </Link>

              {/* Services Mega Menu */}
              <div
                className="relative"
                onMouseEnter={openMenu}
                onMouseLeave={scheduleClose}
              >
                <button
                  className="flex items-center gap-1 text-[15px] text-slate-700 hover:text-[#15803d] font-semibold transition-colors"
                  data-testid="services-dropdown-trigger"
                  onFocus={openMenu}
                >
                  Services
                  <ChevronDown
                    size={15}
                    className={`transition-transform ${isServicesOpen ? 'rotate-180 text-[#15803d]' : ''}`}
                  />
                </button>

                {isServicesOpen && (
                  <>
                    {/* Invisible hover bridge to prevent menu flicker */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full h-4 w-[1100px]" />
                    <div
                      className="absolute top-[calc(100%+0.5rem)] left-1/2 -translate-x-1/2 w-[min(1100px,94vw)] rounded-2xl bg-white/95 backdrop-blur-xl shadow-2xl border border-slate-200/80 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                      data-testid="mega-menu"
                    >
                      {/* Gradient top accent */}
                      <div className="h-1 bg-gradient-to-r from-[#22c55e] via-[#eab308] to-[#f97316]" />

                      <div className="grid grid-cols-12 gap-0">
                        {/* Columns */}
                        <div className="col-span-9 p-7 grid grid-cols-3 gap-6">
                          {serviceGroups.map((group) => (
                            <div key={group.label}>
                              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-4 pl-2">
                                {group.label}
                              </p>
                              <ul className="space-y-1">
                                {group.items.map((item) => {
                                  const Icon = item.icon;
                                  return (
                                    <li key={item.path}>
                                      <Link
                                        to={item.path}
                                        className="group flex items-start gap-3 rounded-xl p-2.5 hover:bg-green-50 transition-all"
                                        data-testid={`service-link-${item.path.replace(/\//g, '')}`}
                                      >
                                        <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 group-hover:from-[#15803d] group-hover:to-[#166534] flex items-center justify-center transition-all">
                                          <Icon
                                            size={16}
                                            className="text-slate-600 group-hover:text-white transition-colors"
                                          />
                                        </span>
                                        <span className="flex-1 min-w-0">
                                          <span className="block text-[13.5px] font-semibold text-slate-900 group-hover:text-[#166534] transition-colors leading-tight">
                                            {item.name}
                                          </span>
                                          <span className="block text-[11.5px] text-slate-500 leading-snug mt-0.5 truncate">
                                            {item.desc}
                                          </span>
                                        </span>
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ))}
                        </div>

                        {/* Featured panel */}
                        <div className="col-span-3 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-7 text-white relative overflow-hidden">
                          <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-[#22c55e]/30 blur-3xl" />
                          <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-[#f97316]/20 blur-3xl" />
                          <div className="relative">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#22c55e] text-slate-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider mb-4">
                              <span className="h-1 w-1 rounded-full bg-slate-900 animate-pulse" />
                              Free
                            </span>
                            <h4 className="font-['Outfit'] text-xl font-bold leading-tight mb-2">
                              Get a FREE SEO Audit
                            </h4>
                            <p className="text-xs text-slate-300 leading-relaxed mb-5">
                              Find out exactly what's holding your site back. No obligation.
                            </p>
                            <QuoteButton
                              data-testid="mega-menu-cta"
                              className="group w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#f97316] hover:bg-[#ea580c] text-white py-2.5 px-4 text-sm font-bold transition-all"
                            >
                              Get My Free Audit
                              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </QuoteButton>
                            <a
                              href="tel:1800006296"
                              className="mt-3 block text-center text-xs text-slate-400 hover:text-white transition-colors"
                            >
                              or call 1800 006 296
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {navigationLinks.slice(1).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-[15px] text-slate-700 hover:text-[#15803d] font-semibold transition-colors"
                >
                  {link.name}
                </Link>
              ))}

              <a
                href="tel:1800006296"
                className="inline-flex items-center gap-1.5 text-[15px] text-slate-900 hover:text-[#15803d] font-bold transition-colors"
                data-testid="header-phone-desktop"
              >
                <Phone size={14} className="text-[#15803d]" /> 1800 006 296
              </a>

              <QuoteButton
                data-testid="cta-button"
                className="bg-[#15803d] hover:bg-[#166534] text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
              >
                Get Started
              </QuoteButton>
            </nav>

            {/* Mobile cluster */}
            <div className="flex items-center gap-3 lg:hidden">
              <a
                href="tel:1800006296"
                className="inline-flex items-center gap-1.5 text-slate-900 font-bold text-sm"
                data-testid="header-phone-mobile"
                aria-label="Call 1800 006 296"
              >
                <span className="w-9 h-9 rounded-full bg-[#15803d] text-white flex items-center justify-center shadow-sm">
                  <Phone size={15} />
                </span>
              </a>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-slate-700 hover:text-[#15803d] transition-colors"
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div
              className="lg:hidden pb-6 animate-in slide-in-from-top duration-200 max-h-[80vh] overflow-y-auto"
              data-testid="mobile-nav"
            >
              <a
                href="tel:1800006296"
                className="flex items-center gap-3 bg-[#15803d] text-white rounded-xl px-4 py-3 mb-4 font-semibold"
                data-testid="mobile-phone-cta"
              >
                <Phone size={18} /> Call 1800 006 296
              </a>

              <div className="flex flex-col gap-1">
                <Link
                  to="/"
                  className="text-slate-700 hover:text-[#15803d] font-semibold py-2.5 px-2"
                >
                  Home
                </Link>

                {/* Mobile Services accordion */}
                <div>
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="flex items-center justify-between w-full text-slate-700 hover:text-[#15803d] font-semibold py-2.5 px-2"
                  >
                    Services
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {mobileServicesOpen && (
                    <div className="ml-2 mt-1 space-y-4 pb-2">
                      {serviceGroups.map((group) => (
                        <div key={group.label}>
                          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 px-2 mb-1">
                            {group.label}
                          </p>
                          <div className="flex flex-col">
                            {group.items.map((item) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className="text-slate-600 hover:text-[#15803d] py-1.5 px-2 text-sm transition-colors"
                              >
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {navigationLinks.slice(1).map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-slate-700 hover:text-[#15803d] font-semibold py-2.5 px-2"
                  >
                    {link.name}
                  </Link>
                ))}

                <QuoteButton
                  data-testid="mobile-cta-button"
                  className="mt-3 bg-[#15803d] hover:bg-[#166534] text-white px-6 py-3 rounded-full font-semibold text-center transition-all"
                >
                  Get Started
                </QuoteButton>
              </div>
            </div>
          )}
        </div>
      </header>

    </>
  );
};

export default Header;
